import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import packageService, { serviceService } from '../../services/packageService';
import { advancedBillingService } from '../../services/advancedBillingService';
import PackageBuilder from './components/PackageBuilder';
import ServiceSelectionPanel from './components/ServiceSelectionPanel';
import PricingOptimization from './components/PricingOptimization';
import PackagePerformanceAnalytics from './components/PackagePerformanceAnalytics';

const ServicePackageIntegration = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    return localStorage?.getItem('language') || 'FR';
  });
  
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('builder');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [packagesData, servicesData, countriesData] = await Promise.all([
        packageService?.getAll(),
        serviceService?.getAll({ isActive: true }),
        advancedBillingService?.getAllCountries()
      ]);
      
      setPackages(packagesData || []);
      setServices(servicesData || []);
      setCountries(countriesData || []);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handlePackageSelect = async (pkg) => {
    setSelectedPackage(pkg);
    if (pkg?.services) {
      setSelectedServices(pkg?.services?.map(s => s?.id));
    }
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices(prev => 
      prev?.includes(serviceId)
        ? prev?.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSavePackage = async (packageData) => {
    try {
      if (selectedPackage) {
        await packageService?.update(selectedPackage?.id, packageData);
        alert('Package mis à jour avec succès');
      } else {
        await packageService?.create(packageData);
        alert('Package créé avec succès');
      }
      await fetchInitialData();
      setSelectedPackage(null);
      setSelectedServices([]);
    } catch (error) {
      console.error('Error saving package:', error);
      alert('Erreur lors de la sauvegarde du package');
    }
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Production', path: '/commercial-packages-management' },
    { label: 'Intégration Services-Packages', path: '/service-package-integration' }
  ];

  const tabs = [
    { id: 'builder', label: 'Package Builder', icon: 'Package' },
    { id: 'analytics', label: 'Performance Analytics', icon: 'BarChart3' }
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
    <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="max-w-7xl mx-auto space-y-6">
            <Breadcrumb items={breadcrumbItems} />
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Intégration Services-Packages</h1>
                <p className="text-muted-foreground mt-1">
                  Configurez les packages commerciaux avec sélection dynamique de services et optimisation des prix
                </p>
              </div>
              <Button
                iconName="RefreshCw"
                variant="outline"
                onClick={fetchInitialData}
              >
                Actualiser
              </Button>
            </div>

            <div className="flex gap-2 border-b border-border">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  {tab?.label}
                </button>
              ))}
            </div>

            {activeTab === 'builder' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <PackageBuilder
                    packages={packages}
                    selectedPackage={selectedPackage}
                    selectedServices={selectedServices}
                    services={services}
                    countries={countries}
                    onPackageSelect={handlePackageSelect}
                    onSavePackage={handleSavePackage}
                  />
                  
                  <PricingOptimization
                    selectedPackage={selectedPackage}
                    selectedServices={selectedServices}
                    services={services}
                  />
                </div>
                
                <div>
                  <ServiceSelectionPanel
                    services={services}
                    selectedServices={selectedServices}
                    onServiceToggle={handleServiceToggle}
                  />
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <PackagePerformanceAnalytics packages={packages} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ServicePackageIntegration;